import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Sparkles, Image, ShoppingBag, Brain, Play, ArrowRight, Star, Shield, Palette, Trophy, Diamond, Crown, Gem } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import FeatureDemoModal from "@/components/FeatureDemoModal";
import HeroCarousel from "@/components/HeroCarousel";
import FeaturedArtists from "@/components/FeaturedArtists";

const tickerItems = [
  "Fine Art Collection", "AI-Powered Analysis", "Verified Artists", "Exclusive Auctions",
  "Expert Curation", "Global Marketplace", "Authenticated Works", "Private Sales",
  "Fine Art Collection", "AI-Powered Analysis", "Verified Artists", "Exclusive Auctions",
  "Expert Curation", "Global Marketplace", "Authenticated Works", "Private Sales",
];


const Index = () => {
  const navigate = useNavigate();
  const [demoFeature, setDemoFeature] = useState<'gallery' | 'analysis' | 'marketplace' | 'collection' | null>(null);
  
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Art Experto | Luxury Art Discovery Platform"
        description="Discover, analyze, and collect exceptional artwork with Art Experto. AI-powered art analysis, curated galleries, secure marketplace, and live auctions for discerning collectors."
        url="https://artexperto.com"
      />
      <Navbar />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-24 md:pt-28 pb-16 md:pb-20 px-4 overflow-hidden">
          {/* Rich background layering */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Radial ambient glows */}
            <div className="absolute top-1/4 left-1/4 w-[700px] h-[700px] bg-gradient-radial opacity-70 animate-float" />
            <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-gradient-radial opacity-50 animate-float animation-delay-300" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-primary/4 blur-3xl" />
            
            {/* Decorative horizontal lines */}
            <div className="absolute top-28 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/25 to-transparent" />
            <div className="absolute bottom-28 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/25 to-transparent" />
            
            {/* Decorative vertical lines */}
            <div className="absolute top-0 left-1/4 w-px h-48 bg-gradient-to-b from-transparent via-primary/20 to-transparent" />
            <div className="absolute top-0 right-1/4 w-px h-48 bg-gradient-to-b from-transparent via-primary/20 to-transparent" />

            {/* Floating diamond accents */}
            <Diamond className="absolute top-40 left-16 h-3 w-3 text-primary/20 animate-sparkle" style={{ animationDelay: '0s' }} />
            <Diamond className="absolute top-60 right-20 h-2 w-2 text-primary/15 animate-sparkle" style={{ animationDelay: '1s' }} />
            <Diamond className="absolute bottom-40 left-1/3 h-2 w-2 text-primary/20 animate-sparkle" style={{ animationDelay: '2s' }} />
            <Diamond className="absolute bottom-60 right-1/3 h-3 w-3 text-primary/15 animate-sparkle" style={{ animationDelay: '0.5s' }} />
          </div>

          <div className="container mx-auto relative z-10">
            {/* Header content */}
            <div className="text-center mb-14">
              <div className="opacity-0 animate-fade-in-up">
                <div className="inline-flex items-center gap-3 mb-8 px-6 py-3 glass rounded-full luxury-border">
                  <Diamond className="h-3.5 w-3.5 text-primary animate-pulse" />
                  <span className="text-elegant text-xs text-primary tracking-luxury">Curated Excellence · Est. 2024</span>
                  <Diamond className="h-3.5 w-3.5 text-primary animate-pulse" />
                </div>
              </div>
              
              <h1 className="opacity-0 animate-fade-in-up animation-delay-200 mb-6">
                <span className="block text-5xl md:text-7xl lg:text-display-lg font-display font-light text-foreground mb-1 tracking-tight">
                  Where Art Meets
                </span>
                <span className="block text-6xl md:text-8xl lg:text-display-xl font-display font-semibold luxury-text text-shadow-luxury">
                  Intelligence
                </span>
              </h1>
              
              {/* Glow line under heading */}
              <div className="opacity-0 animate-fade-in-up animation-delay-400 flex justify-center mb-8">
                <div className="glow-line w-32" />
              </div>

              <div className="opacity-0 animate-fade-in-up animation-delay-400 max-w-2xl mx-auto mb-10">
                <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-light">
                  Art Experto is your <span className="text-foreground font-medium italic font-display">intelligent companion</span> for discovering,
                </p>
                <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-light mt-1">
                  analyzing, and acquiring <span className="luxury-text font-medium">exceptional artwork</span>.
                </p>
              </div>
              
              {/* CTA Buttons */}
              <div className="opacity-0 animate-fade-in-up animation-delay-500 flex flex-col sm:flex-row gap-5 justify-center mb-14">
                <Button 
                  size="lg" 
                  onClick={() => navigate("/gallery")} 
                  className="bg-gradient-hero text-primary-foreground shadow-gold hover:shadow-glow transition-all duration-700 text-base px-12 py-7 group font-medium tracking-wide"
                >
                  <Sparkles className="mr-3 h-5 w-5" />
                  Explore Collection
                  <ArrowRight className="ml-3 h-5 w-5 transition-transform duration-500 group-hover:translate-x-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  onClick={() => navigate("/art-analysis")} 
                  className="text-base px-12 py-7 luxury-border hover:bg-primary/5 transition-all duration-500 font-medium tracking-wide"
                >
                  <Brain className="mr-3 h-5 w-5" />
                  AI Analysis
                </Button>
              </div>
            </div>

            {/* Featured Artworks Carousel */}
            <div className="opacity-0 animate-fade-in-up animation-delay-600 mb-14">
              <HeroCarousel />
            </div>
          </div>
        </section>

        {/* ─── Luxury Ticker Strip ─── */}
        <div className="overflow-hidden border-y border-primary/15 bg-secondary/95 py-4 relative">
          <div className="glow-line absolute top-0 left-0 right-0" />
          <div className="glow-line absolute bottom-0 left-0 right-0" />
          <div className="animate-marquee">
            {tickerItems.map((item, i) => (
              <span key={i} className="flex items-center gap-6 px-8 shrink-0">
                <Diamond className="h-3 w-3 text-primary/70" />
                <span className="text-elegant text-xs text-secondary-foreground/70 tracking-elegant whitespace-nowrap">
                  {item}
                </span>
              </span>
            ))}
          </div>
        </div>


        {/* ─── Trust Indicators ─── */}
        <section className="py-16 px-4 border-b border-border/20">
          <div className="container mx-auto">
            <div className="flex flex-wrap justify-center items-center gap-12 md:gap-20">
              {[
                { icon: Shield, label: "Verified Artists", desc: "Every seller authenticated" },
                { icon: Palette, label: "Curated Selection", desc: "Editorial quality standards" },
                { icon: Trophy, label: "Premium Quality", desc: "Museum-grade artworks" },
                { icon: Crown, label: "Expert Analysis", desc: "AI-powered valuations" },
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center gap-3 group cursor-default max-w-[120px] text-center">
                  <div className="p-4 rounded-2xl bg-primary/5 border border-primary/10 group-hover:bg-primary/10 group-hover:border-primary/25 group-hover:shadow-gold transition-all duration-500">
                    <item.icon className="h-6 w-6 text-primary" />
                  </div>
                  <span className="text-elegant text-xs text-foreground tracking-elegant group-hover:text-primary transition-colors duration-500">
                    {item.label}
                  </span>
                  <span className="text-2xs text-muted-foreground font-light leading-tight">
                    {item.desc}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Artists Section */}
        <FeaturedArtists />

        {/* ─── Features Section ─── */}
        <section className="py-32 px-4 relative overflow-hidden">
          {/* Subtle background texture */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-radial opacity-30" />
          </div>
          <div className="container mx-auto relative z-10">
            <div className="text-center mb-20">
              <div className="inline-flex items-center gap-3 mb-6 px-5 py-2 rounded-full border border-primary/20 bg-primary/5">
                <Gem className="h-3.5 w-3.5 text-primary" />
                <span className="text-elegant text-xs text-primary tracking-luxury">Our Services</span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-medium text-foreground luxury-divider pb-8">
                Elevate Your Art Journey
              </h2>
              <p className="text-muted-foreground font-light max-w-xl mx-auto mt-6">
                A complete suite of tools for the modern art connoisseur — from discovery to acquisition.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Gallery Card */}
              <div 
                className="luxury-card group p-10 rounded-2xl cursor-pointer ornate-corners"
                onClick={() => navigate("/gallery")}
              >
                <div className="absolute inset-0 bg-gradient-radial opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-20 h-20 bg-gradient-hero rounded-2xl flex items-center justify-center shadow-gold group-hover:shadow-glow group-hover:scale-110 transition-all duration-500">
                      <Image className="h-10 w-10 text-primary-foreground" />
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={(e) => { e.stopPropagation(); setDemoFeature('gallery'); }}
                      className="opacity-0 group-hover:opacity-100 transition-all duration-500 text-primary"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Watch Demo
                    </Button>
                  </div>
                  <h3 className="text-3xl font-display font-medium mb-4 group-hover:text-primary transition-colors duration-500">
                    Curated Gallery
                  </h3>
                  <p className="text-muted-foreground leading-relaxed font-light mb-6">
                    Explore meticulously curated collections from world-renowned and emerging artists. 
                    Every piece tells a story worth discovering.
                  </p>
                  <div className="flex items-center gap-2 text-primary text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                    <span className="tracking-wide">Explore Gallery</span>
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </div>

              {/* AI Analysis Card */}
              <div 
                className="luxury-card group p-10 rounded-2xl cursor-pointer ornate-corners"
                onClick={() => navigate("/art-analysis")}
              >
                <div className="absolute inset-0 bg-gradient-radial opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-20 h-20 bg-gradient-accent rounded-2xl flex items-center justify-center group-hover:shadow-glow group-hover:scale-110 transition-all duration-500">
                      <Brain className="h-10 w-10 text-accent-foreground" />
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={(e) => { e.stopPropagation(); setDemoFeature('analysis'); }}
                      className="opacity-0 group-hover:opacity-100 transition-all duration-500 text-accent"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Watch Demo
                    </Button>
                  </div>
                  <h3 className="text-3xl font-display font-medium mb-4 group-hover:text-accent transition-colors duration-500">
                    AI-Powered Insights
                  </h3>
                  <p className="text-muted-foreground leading-relaxed font-light mb-6">
                    Unlock deep understanding with intelligent analysis of style, technique, 
                    provenance, and market context powered by advanced AI.
                  </p>
                  <div className="flex items-center gap-2 text-accent text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                    <span className="tracking-wide">Try Analysis</span>
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </div>

              {/* Marketplace Card */}
              <div 
                className="luxury-card group p-10 rounded-2xl cursor-pointer ornate-corners"
                onClick={() => navigate("/marketplace")}
              >
                <div className="absolute inset-0 bg-gradient-radial opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-20 h-20 bg-gradient-luxury rounded-2xl flex items-center justify-center group-hover:shadow-glow group-hover:scale-110 transition-all duration-500">
                      <ShoppingBag className="h-10 w-10 text-secondary-foreground" />
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={(e) => { e.stopPropagation(); setDemoFeature('marketplace'); }}
                      className="opacity-0 group-hover:opacity-100 transition-all duration-500"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Watch Demo
                    </Button>
                  </div>
                  <h3 className="text-3xl font-display font-medium mb-4 group-hover:text-primary transition-colors duration-500">
                    Exclusive Marketplace
                  </h3>
                  <p className="text-muted-foreground leading-relaxed font-light mb-6">
                    Acquire unique masterpieces through our secure, verified marketplace. 
                    Every transaction backed by our authenticity guarantee.
                  </p>
                  <div className="flex items-center gap-2 text-primary text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                    <span className="tracking-wide">Browse Marketplace</span>
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </div>

              {/* Collection Card */}
              <div 
                className="luxury-card group p-10 rounded-2xl cursor-pointer ornate-corners"
                onClick={() => navigate("/my-artworks")}
              >
                <div className="absolute inset-0 bg-gradient-radial opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-20 h-20 bg-gradient-gold rounded-2xl flex items-center justify-center shadow-gold group-hover:shadow-glow group-hover:scale-110 transition-all duration-500">
                      <Sparkles className="h-10 w-10 text-primary-foreground" />
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={(e) => { e.stopPropagation(); setDemoFeature('collection'); }}
                      className="opacity-0 group-hover:opacity-100 transition-all duration-500 text-primary"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Watch Demo
                    </Button>
                  </div>
                  <h3 className="text-3xl font-display font-medium mb-4 group-hover:text-primary transition-colors duration-500">
                    Personal Collection
                  </h3>
                  <p className="text-muted-foreground leading-relaxed font-light mb-6">
                    Manage your art portfolio with sophisticated tools. Track value, 
                    organize exhibitions, and share your passion with the world.
                  </p>
                  <div className="flex items-center gap-2 text-primary text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                    <span className="tracking-wide">My Collection</span>
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Testimonial / Quote Strip ─── */}
        <section className="py-20 px-4 bg-muted/20 border-y border-border/20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-full h-px glow-line opacity-40" />
            <div className="absolute bottom-0 left-0 w-full h-px glow-line opacity-40" />
          </div>
          <div className="container mx-auto text-center max-w-3xl">
            <div className="text-5xl font-display text-primary/30 mb-4 leading-none">"</div>
            <blockquote className="text-2xl md:text-3xl font-display font-light text-foreground leading-relaxed italic mb-6">
              Art is not what you see, but what you make others see.
            </blockquote>
            <div className="flex justify-center items-center gap-4">
              <div className="h-px w-12 bg-gradient-gold" />
              <span className="text-elegant text-xs text-muted-foreground tracking-luxury">Edgar Degas</span>
              <div className="h-px w-12 bg-gradient-gold" />
            </div>
          </div>
        </section>

        {/* ─── CTA Section ─── */}
        <section className="py-32 px-4 relative overflow-hidden">
          <div className="container mx-auto">
            <div className="relative overflow-hidden rounded-3xl p-16 md:p-24 bg-gradient-noir border border-primary/15 shadow-strong">
              {/* Decorative elements */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-primary/8 rounded-full blur-3xl" />
                <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-primary/8 rounded-full blur-3xl" />
                <div className="absolute top-0 left-0 w-full h-px glow-line opacity-50" />
                <div className="absolute bottom-0 left-0 w-full h-px glow-line opacity-50" />
                {/* Ornate corners on the CTA card */}
                <div className="absolute top-6 left-6 w-10 h-10 border-t-2 border-l-2 border-primary/25 rounded-tl-lg" />
                <div className="absolute top-6 right-6 w-10 h-10 border-t-2 border-r-2 border-primary/25 rounded-tr-lg" />
                <div className="absolute bottom-6 left-6 w-10 h-10 border-b-2 border-l-2 border-primary/25 rounded-bl-lg" />
                <div className="absolute bottom-6 right-6 w-10 h-10 border-b-2 border-r-2 border-primary/25 rounded-br-lg" />
              </div>
              
              <div className="relative z-10 text-center max-w-3xl mx-auto">
                <div className="flex justify-center gap-3 mb-8">
                  <Diamond className="h-5 w-5 text-primary animate-pulse-gold" />
                  <Diamond className="h-8 w-8 text-primary animate-pulse-gold" style={{ animationDelay: '0.4s' }} />
                  <Diamond className="h-5 w-5 text-primary animate-pulse-gold" style={{ animationDelay: '0.8s' }} />
                </div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-medium mb-6 text-secondary-foreground leading-tight">
                  Begin Your Collection
                  <span className="block luxury-text text-shadow-luxury">Today</span>
                </h2>
                <div className="glow-line w-24 mx-auto mb-8" />
                <p className="text-xl mb-12 text-secondary-foreground/60 leading-relaxed font-light max-w-lg mx-auto">
                  Join an exclusive community of collectors and art enthusiasts. 
                  Your next masterpiece awaits.
                </p>
                <div className="flex flex-col sm:flex-row gap-5 justify-center">
                  <Button 
                    size="lg" 
                    onClick={() => navigate("/auth")} 
                    className="bg-gradient-hero text-primary-foreground hover:opacity-90 text-base px-12 py-7 shadow-gold hover:shadow-glow transition-all duration-500 font-medium tracking-wide group"
                  >
                    Create Free Account
                    <ArrowRight className="ml-3 h-5 w-5 transition-transform duration-500 group-hover:translate-x-1" />
                  </Button>
                  <Button 
                    size="lg" 
                    variant="secondary" 
                    onClick={() => navigate("/gallery")}
                    className="text-base px-12 py-7 transition-all duration-500 font-medium tracking-wide"
                  >
                    Browse Gallery
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      
      <FeatureDemoModal open={demoFeature !== null} onOpenChange={open => !open && setDemoFeature(null)} feature={demoFeature} />
    </div>
  );
};

export default Index;
